import { apiClient } from './client';
import type {
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  RefreshResponse,
  User,
} from '@/shared/types/auth';

/**
 * Authentication API methods.
 */
export const authApi = {
  /**
   * Login with email and password.
   */
  login: async (data: LoginRequest): Promise<AuthResponse> => {
    const response = await apiClient.post<AuthResponse>('/auth/login', data);
    return response.data;
  },

  /**
   * Register new user.
   */
  register: async (data: RegisterRequest): Promise<AuthResponse> => {
    const response = await apiClient.post<AuthResponse>('/auth/register', data);
    return response.data;
  },

  /**
   * Refresh access token using HTTP-only refresh token cookie.
   */
  refresh: async (): Promise<RefreshResponse> => {
    const response = await apiClient.post<RefreshResponse>('/auth/refresh');
    return response.data;
  },

  /**
   * Get current user profile.
   */
  me: async (): Promise<User> => {
    const response = await apiClient.get<User>('/auth/me');
    return response.data;
  },

  /**
   * Logout user and invalidate tokens.
   */
  logout: async (): Promise<void> => {
    await apiClient.post('/auth/logout');
  },

  /**
   * Change the password of the signed-in user.
   *
   * Отличается от resetPassword: тот доверяет одноразовому токену из письма,
   * этот — знанию действующего пароля. Экрана смены не было вовсе, и человеку
   * приходилось выходить и притворяться забывшим пароль.
   */
  changePassword: async (currentPassword: string, newPassword: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  },

  /**
   * Delete the signed-in user's account. Irreversible.
   *
   * Уносит организации, где человек единственный участник, вместе с
   * проектами, источниками и подпиской. Восстановление — только из ночного
   * бэкапа и только через поддержку.
   */
  deleteAccount: async (password: string): Promise<{ message: string }> => {
    const response = await apiClient.delete<{ message: string }>('/auth/me', {
      data: { password },
    });
    return response.data;
  },

  /**
   * Get Yandex OAuth URL.
   */
  getYandexOAuthUrl: (): string => {
    return `${apiClient.defaults.baseURL}/auth/yandex`;
  },

  /**
   * Handle OAuth callback.
   */
  handleOAuthCallback: async (code: string, state: string): Promise<AuthResponse> => {
    const response = await apiClient.get<AuthResponse>('/auth/yandex/callback', {
      params: { code, state },
    });
    return response.data;
  },

  /**
   * Request password reset email.
   */
  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>('/auth/forgot-password', { email });
    return response.data;
  },

  /**
   * Reset password with token.
   */
  resetPassword: async (token: string, newPassword: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>('/auth/reset-password', {
      token,
      new_password: newPassword,
    });
    return response.data;
  },

  /**
   * Get integrations status (Yandex OAuth connection).
   */
  getIntegrations: async (): Promise<{ yandex: { connected: boolean } }> => {
    const response = await apiClient.get<{ yandex: { connected: boolean } }>('/auth/integrations');
    return response.data;
  },

  /**
   * Get detailed integration status (individual service scopes).
   */
  getIntegrationStatus: async (): Promise<IntegrationStatus> => {
    const response = await apiClient.get<IntegrationStatus>('/auth/integrations/status');
    return response.data;
  },

  /**
   * Disconnect Yandex OAuth.
   */
  disconnectYandex: async (): Promise<{ message: string }> => {
    const response = await apiClient.delete<{ message: string }>('/auth/integrations/yandex');
    return response.data;
  },

  /**
   * Get URL to connect Metrika (requires metrika:read scope).
   */
  getMetrikaConnectUrl: (): string => {
    return `${apiClient.defaults.baseURL}/auth/connect/metrika`;
  },

  /**
   * Get URL to connect Webmaster (requires webmaster:verify scope).
   */
  getWebmasterConnectUrl: (): string => {
    return `${apiClient.defaults.baseURL}/auth/connect/webmaster`;
  },

  /**
   * Complete service connection after OAuth callback.
   */
  completeServiceConnection: async (
    code: string,
    state: string
  ): Promise<{ message: string; integrations: IntegrationStatus }> => {
    const response = await apiClient.post<{ message: string; integrations: IntegrationStatus }>(
      '/auth/connect/complete',
      {
        code,
        state,
      }
    );
    return response.data;
  },
};

/**
 * Integration status for individual Yandex services.
 */
export interface IntegrationStatus {
  yandex: boolean; // Basic Yandex account linked
  metrika: boolean; // Metrika scope granted
  webmaster: boolean; // Webmaster scope granted
}
